import type { ActivatedRoute } from '@angular/router';
import { loadIntFilterFromGetParams, loadStringFilterFromGetParams } from '../reporting-core/utils';

export enum RedemptionCodeSetProductType {
  membership = 'MEM',
  entryPass = 'ENP',
  voucherPack = 'VOP',
}

export enum RedemptionCodeSetStatus {
  available = 'AVA',
  expired = 'EXP',
  depleted = 'DEP',
}

export enum RedemptionCodeStatus {
  available = 'AVA',
  redeemed = 'RED',
  expired = 'EXP',
}

export interface RedemptionCodeSet {
  id: number;
  url: string;
  name: string;
  product_type: RedemptionCodeSetProductType;
  product_id: number;
  product_name: string;
  status: RedemptionCodeSetStatus,
  num_codes: number;
  num_codes_available: number;
  expires_at: string | null;
  expiration_date: string | null;
  created_by_id: number | null;
  created_by_first_name: string | null;
  created_by_last_name: string | null;
  created_by_email: string | null;
  last_updated_by_id: number | null;
  last_updated_by_first_name: string | null;
  last_updated_by_last_name: string | null;
  last_updated_by_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface RedemptionCode {
  id: number;
  url: string;
  code: string;
  redemption_url: string;
  status: RedemptionCodeStatus;
  redeemed_by_id: number | null;
  redeemed_by_first_name: string | null;
  redeemed_by_last_name: string | null;
  redeemed_by_birthday: string | null;
  redeemed_by_email: string | null;
  redeemed_for_id: number | null;
  redeemed_for_first_name: string | null;
  redeemed_for_last_name: string | null;
  redeemed_for_birthday: string | null;
  redeemed_for_email: string | null;
  redeemed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface RedemptionCodeReportItem extends RedemptionCode {
  redemption_code_set_id: number;
  redemption_code_set_name: string;
  redemption_code_set_status: RedemptionCodeSetStatus;
  product_type: RedemptionCodeSetProductType;
  product_id: number;
  product_name: string;
  redemption_location_id: number | null;
  redemption_location_name: string | null;
}

export interface RedemptionCodeSetFilters {
  product_type?: RedemptionCodeSetProductType;
  status?: RedemptionCodeSetStatus;
}

export interface RedemptionCodeReportFilters {
  redemption_code_set_id?: number;
  status?: RedemptionCodeStatus;
  created_at_min?: string;
  created_at_max?: string;
  created_at_range?: string;
  redeemed_at_min?: string;
  redeemed_at_max?: string;
  redeemed_at_range?: string;
  redemption_location_id?: number;
  product_type_in?: string;
}

export function loadRedemptionCodeSetFiltersFromGetParams(activatedRoute: ActivatedRoute): RedemptionCodeSetFilters {
  const filters: RedemptionCodeSetFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'product_type', initialQPs);
  loadStringFilterFromGetParams(filters, 'status', initialQPs);

  return filters;
}

export function loadRedemptionCodeReportFiltersFromGetParams(activatedRoute: ActivatedRoute): RedemptionCodeReportFilters {
  const filters: RedemptionCodeReportFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadIntFilterFromGetParams(filters, 'redemption_code_set_id', initialQPs);
  loadStringFilterFromGetParams(filters, 'status', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_range', initialQPs);
  loadStringFilterFromGetParams(filters, 'redeemed_at_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'redeemed_at_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'redeemed_at_range', initialQPs);
  loadIntFilterFromGetParams(filters, 'redemption_location_id', initialQPs);
  loadStringFilterFromGetParams(filters, 'product_type_in', initialQPs);

  return filters;
}

export function getRedemptionCodeSetProductTypeDisplay(productType: RedemptionCodeSetProductType | string): string {
  switch (productType) {
    case RedemptionCodeSetProductType.membership:
      return 'Membership';
    case RedemptionCodeSetProductType.entryPass:
      return 'Entry Pass';
    case RedemptionCodeSetProductType.voucherPack:
      return 'Voucher Pack';
    default:
      return 'Unknown';
  }
}

export function getRedemptionCodeSetStatusDisplay(status: RedemptionCodeSetStatus | string): string {
  switch (status) {
    case RedemptionCodeSetStatus.available:
      return 'Available';
    case RedemptionCodeSetStatus.depleted:
      return 'Depleted';
    case RedemptionCodeSetStatus.expired:
      return 'Expired';
    default:
      return 'Unknown';
  }
}

export function getRedemptionCodeStatusDisplay(status: RedemptionCodeStatus): string {
  switch (status) {
    case RedemptionCodeStatus.available:
      return 'Available';
    case RedemptionCodeStatus.redeemed:
      return 'Redeemed';
    case RedemptionCodeStatus.expired:
      return 'Expired';
    default:
      return 'Unknown';
  }
}

/**
 * Get the display text for a redemption code's status, accounting for the
 * status of its set: codes in an expired set display as "Expired" even though
 * their own status remains "Available", and individually-expired codes
 * display as "Manually Expired". This mirrors the status display on the
 * redemption code set page and its codes export.
 */
export function getRedemptionCodeReportStatusDisplay(codeStatus: RedemptionCodeStatus,
                                                     setStatus: RedemptionCodeSetStatus): string {
  switch (codeStatus) {
    case RedemptionCodeStatus.available:
      if (setStatus === RedemptionCodeSetStatus.expired) {
        return 'Expired';
      }
      return 'Available';
    case RedemptionCodeStatus.redeemed:
      return 'Redeemed';
    case RedemptionCodeStatus.expired:
      return 'Manually Expired';
    default:
      return 'Unknown';
  }
}
